import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAgents } from './hooks';
import { useAgentOptions } from './hooks/useAgentOptions';
import { AgentTable, SearchAndFilters } from './components';
import { Agent } from './types';
import { Button, PageLoader, FullWidthContainer, Modal } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { useQuota } from '../../hooks/useQuota';

const Agents: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agents, loading, error, filters, setFilters, deleteAgent, toggleAgentStatus } =
    useAgents();
  const { voices, loading: optionsLoading } = useAgentOptions();
  const { quota } = useQuota();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    agent: Agent | null;
  }>({ isOpen: false, agent: null });

  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const handleCreateAgent = () => {
    navigate('/agents/create');
  };

  const handleEditAgent = (agent: Agent) => {
    navigate(`/agents/edit/${agent.id}`);
  };

  const handleToggleStatus = async (agent: Agent) => {
    await toggleAgentStatus(agent.id);
  };

  const handleDeleteAgent = (agent: Agent) => {
    setDeleteModal({ isOpen: true, agent });
  };

  const confirmDelete = async () => {
    if (deleteModal.agent) {
      await deleteAgent(deleteModal.agent.id);
      setDeleteModal({ isOpen: false, agent: null });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, agent: null });
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Agent Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your AI agents and their configurations
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500 text-center">
            <p className="text-lg font-semibold">Error loading agents</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('agents.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('agents.subtitle')}</p>
            {quota?.max_agents != null && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {quota.current_agents} / {quota.max_agents} {t('quota.agents')}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="gradient-multi"
              onClick={handleCreateAgent}
              disabled={quota?.agents_limit_reached === true}
              title={quota?.agents_limit_reached ? t('quota.agentLimitReached') : undefined}
              className="flex items-center gap-2"
            >
              <span className="material-icons">add</span>
              {t('agents.createAgent')}
            </Button>
          </div>
        </div>

        {/* Limit warning banner */}
        {quota?.agents_limit_reached && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300">
            <span className="material-icons text-lg">warning</span>
            <span className="text-sm">{t('quota.agentLimitReached')}</span>
          </div>
        )}

        {/* Search and Filters */}
        <SearchAndFilters
          filters={filters}
          onFiltersChange={setFilters}
          voices={voices}
          voicesLoading={optionsLoading}
        />

        {/* Agents Table */}
        <AgentTable
          agents={agents}
          onEdit={handleEditAgent}
          onToggleStatus={handleToggleStatus}
          onDelete={handleDeleteAgent}
          onCreateAgent={handleCreateAgent}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={cancelDelete}
        title=""
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center space-y-6">
          {/* Warning Icon */}
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <span className="material-icons text-3xl text-red-600 dark:text-red-400">
              delete_forever
            </span>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('agents.deleteAgent')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('agents.areYouSureDeleteAgent')}{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                &ldquo;{deleteModal.agent?.name}&rdquo;
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('agents.thisActionCannotBeUndoneAgent')}
            </p>
          </div>

          {/* Agent Info Card */}
          {deleteModal.agent && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                    smart_toy
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {deleteModal.agent.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {deleteModal.agent.language?.title || 'No language'} •{' '}
                    {deleteModal.agent.voice?.name || 'No voice'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={cancelDelete}
              className="flex-1 px-6 py-3 font-medium"
            >
              {t('agents.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              className="flex-1 px-6 py-3 font-medium"
            >
              <span className="material-icons text-sm mr-2">delete</span>
              {t('agents.deleteAgent')}
            </Button>
          </div>
        </div>
      </Modal>
    </FullWidthContainer>
  );
};

export { Agents };
